import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { BackButton } from '@/components/common/BackButton';
import { ErrorState } from '@/components/common/ErrorState';
import { SubmitterDetails } from '@/components/common/SubmitterDetails';
import { PageTitle } from '@/components/layout/PageTitle';
import { PageToolbar } from '@/components/layout/PageToolbar';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TeacherProfileDetails,
  TeacherProfileDetailsSkeleton,
} from '@/features/teacherSubmissions/components/TeacherProfileDetails';
import { TEACHER_PROFILE_SCROLL_AREA_CLASS } from '@/features/teacherSubmissions/constants/teacherSubmission.constants';
import { useHistoryBack } from '@/hooks/useHistoryBack';
import { useRememberViewedRow } from '@/hooks/useLastViewedRow';
import { cn } from '@/lib/utils';
import { teacherInactivationDetailQueryOptions } from '../api/teacherInactivations.api';
import { TeacherInactivationRequestSection } from '../components/TeacherInactivationRequestSection';
import { TeacherInactivationReviewPanel } from '../components/TeacherInactivationReviewPanel';
import { TeacherInactivationStatusBadge } from '../components/TeacherInactivationStatusBadge';
import { TEACHER_INACTIVATION_LIST_PATH } from '../constants/teacherInactivation.constants';

const PANEL_CLASS =
  'relative flex shrink-0 flex-col gap-6 overflow-hidden rounded-xl border bg-card p-4 shadow-xs';
const PAGE_BODY_CLASS = cn(TEACHER_PROFILE_SCROLL_AREA_CLASS, 'flex flex-col');
const HEADER_BADGE_CLASS = 'font-semibold max-sm:h-6 max-sm:px-2 max-sm:text-xs-plus';
const LOADING_TITLE = 'Teacher Inactivation';

interface TeacherInactivationDetailPageProps {
  requestId: number;
}

export function TeacherInactivationDetailPage({ requestId }: TeacherInactivationDetailPageProps) {
  const navigate = useNavigate();
  const {
    data: request,
    isFetching,
    error,
    refetch,
  } = useQuery(teacherInactivationDetailQueryOptions(requestId));
  const handleBack = useHistoryBack(() => void navigate({ to: TEACHER_INACTIVATION_LIST_PATH }));

  useRememberViewedRow('teacherInactivations', requestId);

  const backButton = <BackButton onClick={handleBack} />;

  if (error) {
    return (
      <ErrorState
        error={error}
        fallbackTitle="Could not load this request"
        onRetry={() => void refetch()}
        isRetrying={isFetching}
        action={backButton}
        fill
      />
    );
  }

  if (!request) return <TeacherInactivationDetailSkeleton backButton={backButton} />;

  const decisionPanel = (
    <div className={PANEL_CLASS}>
      <TeacherInactivationRequestSection request={request} />
      <TeacherInactivationReviewPanel request={request} />
    </div>
  );

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <header className="flex flex-col gap-2">
        <PageTitle leading={backButton}>{request.teacherName}</PageTitle>
        <PageToolbar
          currentTitle={request.teacherName}
          breadcrumbAddon={
            <TeacherInactivationStatusBadge
              status={request.status}
              className={HEADER_BADGE_CLASS}
            />
          }
        />
        <SubmitterDetails
          name={request.requesterName}
          hrEmpId={request.requesterHrEmpId}
          amName={request.amName}
          submittedAt={request.createdAt}
        />
      </header>

      <div className={PAGE_BODY_CLASS}>
        {request.teacher ? (
          <TeacherProfileDetails teacher={request.teacher} detailsHeader={decisionPanel} />
        ) : (
          decisionPanel
        )}
      </div>
    </div>
  );
}

interface TeacherInactivationDetailSkeletonProps {
  backButton: ReactNode;
}

function TeacherInactivationDetailSkeleton({ backButton }: TeacherInactivationDetailSkeletonProps) {
  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <header className="flex flex-col gap-2">
        <PageTitle leading={backButton}>{LOADING_TITLE}</PageTitle>
        <PageToolbar currentTitle={<Skeleton className="h-4 w-32" />} />
        <Skeleton className="h-6 w-72 max-w-full" />
      </header>
      <div className={PAGE_BODY_CLASS}>
        <TeacherProfileDetailsSkeleton
          detailsHeader={
            <div className={PANEL_CLASS}>
              <Skeleton className="h-16 w-full rounded-lg" />
              <div className="flex justify-end gap-2">
                <Skeleton className="h-9 w-24" />
                <Skeleton className="h-9 w-28" />
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
}
